import { useMemo, useState } from 'react';
import { Barcode, Package, Plus, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { asignarCodigoProducto } from '@/services/products';
import { Product } from '@/types';
import { formatPrice } from '@/utils/format';
import { Unit, pricePerDisplayFromBase } from '@/utils/units';
import { matchesAllWords } from '@/utils/search';

interface Props {
  /** Código escaneado que no coincidió con ningún producto. null = modal cerrado. */
  code: string | null;
  /** Catálogo donde buscar (ya cargado por la pantalla, para no pedirlo de nuevo). */
  products: Product[];
  onClose: () => void;
  /** Se llama al asignar, para refrescar la pantalla. */
  onAssigned?: (product: Product) => void;
  /** Si se pasa, ofrece dar de alta un producto nuevo con ese código. */
  onCreateNew?: (code: string) => void;
}

/**
 * Escaneaste un código que no está en ningún producto. En vez de un simple
 * "no encontrado", te deja resolverlo en el momento: asignárselo a un producto
 * existente o dar de alta uno nuevo ya con el código puesto.
 *
 * Pensado para cuando se está cargando el catálogo: pistola en mano, producto
 * por producto, sin ir a buscar cada uno al ABM.
 */
export default function AsignarCodigoModal({
  code, products, onClose, onAssigned, onCreateNew,
}: Props) {
  const [busqueda, setBusqueda] = useState('');
  const [confirmando, setConfirmando] = useState<Product | null>(null);
  const queryClient = useQueryClient();
  // Endpoint dedicado (permiso `inventory`), asi el empleado tambien puede
  // asignar codigos sin tener acceso al ABM de productos.
  const updateProduct = useMutation({
    mutationFn: ({ id, codigo }: { id: number; codigo: string }) =>
      asignarCodigoProducto(id, codigo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });

  const resultados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    // Buscando: TODAS las coincidencias (la lista scrollea). Sin buscar, solo un
    // arranque, que mostrar el catálogo entero no ayuda a nadie.
    return q ? products.filter((p) => matchesAllWords(q, p.name, p.code)) : products.slice(0, 20);
  }, [products, busqueda]);

  const asignar = async (product: Product) => {
    try {
      await updateProduct.mutateAsync({ id: product.id, codigo: code || '' });
      toast.success(`Código asignado a ${product.name}`);
      onAssigned?.(product);
      cerrar();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'No se pudo asignar el código');
    }
  };

  const cerrar = () => {
    setBusqueda('');
    setConfirmando(null);
    onClose();
  };

  return (
    <Modal isOpen={!!code} onClose={cerrar} title="Código sin asignar" size="lg">
      <div className="space-y-4">
        <div className="flex items-center gap-3 rounded-xl bg-amber-50 border border-amber-200 p-3">
          <Barcode className="h-6 w-6 text-amber-600 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm text-amber-800">Este código no está en ningún producto:</p>
            <p className="font-mono font-bold text-amber-900 break-all">{code}</p>
          </div>
        </div>

        {/* Confirmación cuando el producto elegido YA tenía un código: se pisa, y
            conviene que se vea, porque el código viejo deja de funcionar. */}
        {confirmando ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              <strong>{confirmando.name}</strong> ya tiene el código{' '}
              <span className="font-mono">{confirmando.code}</span>. Si continuás, queda
              con el nuevo y el anterior deja de servir para escanear.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirmando(null)}>Volver</Button>
              <Button onClick={() => asignar(confirmando)} loading={updateProduct.isPending}>
                Reemplazar código
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                autoFocus
                type="text"
                placeholder="Buscar el producto por nombre…"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary-400"
              />
            </div>

            <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 max-h-96 overflow-y-auto">
              {resultados.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">
                  Ningún producto coincide con "{busqueda}"
                </p>
              ) : (
                <>
                <p className="px-3 py-1.5 text-xs text-gray-500 bg-gray-50 sticky top-0">
                  {busqueda.trim()
                    ? `${resultados.length} coincidencia${resultados.length === 1 ? '' : 's'}`
                    : 'Escribí para buscar — se muestran los primeros 20'}
                </p>
                {(
                resultados.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    disabled={updateProduct.isPending}
                    onClick={() => (p.code ? setConfirmando(p) : asignar(p))}
                    className="w-full flex items-start justify-between gap-3 px-3 py-2.5 text-left hover:bg-gray-50 disabled:opacity-50"
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <Package className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        {/* Sin truncar: los nombres largos son justo los que hay que
                            leer enteros para no asignarle el código al producto equivocado */}
                        <p className="font-medium text-gray-800 leading-tight">{p.name}</p>
                        <p className="text-xs text-gray-500">
                          {p.code ? `Código actual: ${p.code}` : 'Sin código'}
                        </p>
                      </div>
                    </div>
                    {/* El precio ayuda a distinguir presentaciones parecidas del mismo producto */}
                    <span className="text-sm font-bold text-emerald-700 tabular-nums whitespace-nowrap flex-shrink-0">
                      {formatPrice(pricePerDisplayFromBase(
                        Number(p.sale_price || 0), (p.display_unit as Unit) || 'unit',
                      ))}
                    </span>
                  </button>
                )))}
                </>
              )}
            </div>

            {onCreateNew && (
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => { if (code) { onCreateNew(code); cerrar(); } }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Crear un producto nuevo con este código
              </Button>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
